import {createApp, ref, computed} from "vue";
import * as Sentry from "@sentry/vue";
import i18n from "../plugins/i18n";
import {isTablet, getDeviceType} from "../plugins/Helpers";
import {Loader, VoucherSalesModalElement, ContactFormModal, ErrorModal} from "@webstacz/shop-components";
import DesktopAr from "../components/DesktopAr.vue";
import EcoMobilierModal from "../components/EcoMobilierModal.vue";

document.addEventListener('DOMContentLoaded', () => {
    const modals = document.querySelectorAll('.open-product-contact-form-modal, .open-contact-form-modal');
    for (let [modalIndex, modal] of modals.entries()) {
        let button = modal;
        const el = document.createElement('div');
        document.querySelector('main').append(el);

        const app = createApp({
            components: {
                ContactFormModal,
                Loader,
            },
            template:
                `
                    <div :class="{'is-responsive':isResponsive}">
                        <ContactFormModal
                            v-model="showModal"
                            @close="hideModal"
                            :config="config"
                            :topOffset="headerHeight"
                            @loadingChanged="loader = $event"
                        />
                        <Loader :visible="loader"/>
                    </div>
                `,
            setup() {
                const loader = ref(false);
                const showModal = ref(false);
                const headerHeight = ref(0);
                const config = ref(JSON.parse(button.getAttribute('data-config')));

                const isResponsive = computed(() => {
                    return isTablet();
                });

                const hideModal = () => {
                    showModal.value = false;
                };

                return {
                    loader,
                    showModal,
                    headerHeight,
                    config,
                    isResponsive,
                    hideModal,
                };
            },
        });

        app.config.idPrefix = `cfm-${modalIndex}`;
        app.use(i18n);
        const vm = app.mount(el);

        button.addEventListener('click', (e) => {
            e.preventDefault();
            vm.showModal = true;
            const header = document.getElementById('header');
            if (header) {
                vm.headerHeight = header.clientHeight;
            }
        });
    }
});

document.addEventListener('DOMContentLoaded', () => {
    const modalSelector = document.querySelectorAll('.open-voucher-sales-modal');
    for (const button of modalSelector) {
        let vm;
        if (!vm) {
            const el = document.createElement('div');
            document.body.append(el);

            const app = createApp({
                components: {
                    VoucherSalesModalElement,
                },
                template: `
                    <VoucherSalesModalElement
                        v-model="showModal"
                        :modal-configuration="modalConfiguration"
                    />
                `,
                setup() {
                    const showModal = ref(false);
                    const modalConfiguration = ref(JSON.parse(button.getAttribute('data-modal-configuration')));

                    return {
                        showModal,
                        modalConfiguration,
                    };
                },
            });

            app.use(i18n);
            vm = app.mount(el);

            button.addEventListener('click', (e) => {
                e.preventDefault();
                vm.showModal = true;
            });
        }
    }
});

document.addEventListener('DOMContentLoaded', () => {
    if (!window.bannerVoucher) {
        return;
    }

    const el = document.createElement('div');
    document.body.append(el);

    const app = createApp({
        components: {
            VoucherSalesModalElement,
        },
        template: `
                <VoucherSalesModalElement
                    v-model="showModal"
                    :modal-configuration="modalConfiguration"
                />
            `,
        setup() {
            const showModal = ref(false);
            const modalConfiguration = ref(window.bannerVoucher);

            return {
                showModal,
                modalConfiguration,
            };
        },
    });

    app.use(i18n);
    const vm = app.mount(el);

    document.addEventListener('openHeaderBarVoucherPopup', (e) => {
        vm.modalConfiguration.allow_activate = e.detail?.showAddCodeButton ?? true;
        vm.showModal = true;
    });
});

document.addEventListener('DOMContentLoaded', () => {
    for (const openModalButton of document.getElementsByClassName('pfc-show-ar')) {
        if (openModalButton) {
            const config = JSON.parse(openModalButton.dataset.config);
            const configuratorUrl = openModalButton.dataset.configuratorUrl;
            const createdType = openModalButton.dataset.createdType;
            // on desktop show QR code (vue)
            if (getDeviceType() === 'desktop') {
                let el = jQuery('<div></div>').insertAfter('main');

                const app = createApp({
                    components: {
                        DesktopArModal: DesktopAr,
                        Loader,
                    },
                    template: `
                        <div>
                            <DesktopArModal
                                :class="{'is-responsive': isTablet()}"
                                v-if="showModal"
                                @hide="showModal = false"
                                @sendEmail="arDesktopSendEmail"
                                ref="modal"/>
                            <Loader :visible="loader"/>
                        </div>
                    `,
                    setup() {
                        const showModal = ref(false);
                        const loader = ref(false);
                        const modal = ref(null);

                        const arDesktopSendEmail = async (formData) => {
                            loader.value = true;
                            try {
                                axios.defaults.headers.common['X-CSRF-TOKEN'] = window['csrfToken'];
                                const {data} = await axios.post(
                                    config.url,
                                    {
                                        email: formData.email,
                                        parent_configuration_id: config.parent_configuration_id,
                                        created_type: createdType,
                                        product_id: config.configurable_product_id,
                                        terms_and_conditions: formData.newsletter,
                                        price: config.product_price,
                                    });

                                if (data.status !== 'ok') {
                                    modal.value.emailError = true;
                                }

                                pushDatalayer(data.hash, formData.email, data.id);
                                pushAfterSaveDatalayer(data.after_save_datalayer_event, data.customer_configuration_count, formData.email, data.id)
                                formData.done(data.url + '?loadAr=true');
                            } catch (e) {
                                console.error(e);
                                Sentry.captureException(e);
                            } finally {
                                loader.value = false;
                            }
                        };

                        const pushDatalayer = (configurationHash, email, configurationId) => {
                            const event = new CustomEvent('productSaveModalSentEvent', {
                                detail: {
                                    product_id: config.product_id,
                                    product_type: config.product_type,
                                    product_price: config.product_price,
                                    parent_configuration_id: config.parent_configuration_id,
                                    configuration: configurationHash,
                                    location: 'pfc',
                                    email,
                                    configuration_id: configurationId,
                                },
                            });
                            document.dispatchEvent(event);
                        };

                        const pushAfterSaveDatalayer = (datalayerEvent, customerConfigurationCount, email, configurationId) => {
                            const event = new CustomEvent('productConfigurationAfterSave', {
                                detail: {
                                    event: datalayerEvent,
                                    product_type: config.product_type,
                                    product_price: config.product_price,
                                    configuration_count: customerConfigurationCount,
                                    email,
                                    configuration_id: configurationId,
                                }
                            });
                            document.dispatchEvent(event);
                        }

                        return {
                            showModal,
                            loader,
                            modal,
                            arDesktopSendEmail,
                            isTablet,
                        };
                    },
                });

                app.use(i18n);
                const vm = app.mount(el[0]);

                openModalButton.addEventListener('click', function (e) {
                    e.preventDefault();
                    vm.showModal = true;
                });
            } else {
                // mobile - redirect to configurator with AR
                openModalButton.addEventListener('click', function (e) {
                    e.preventDefault();
                    window.location.href = configuratorUrl + '?loadAr=true';
                });
            }
        }
    }
});

document.addEventListener('DOMContentLoaded', () => {
    const el = document.querySelector('#eco-mobilier-modal-activator');
    if (el) {
        const app = createApp(EcoMobilierModal, {
            price: el.getAttribute('data-price'),
            label: el.getAttribute('data-label'),
        });

        app.use(i18n);
        app.mount(el);
    }

});

document.addEventListener('DOMContentLoaded', () => {
    const el = document.createElement('div');
    document.querySelector('main').append(el);

    const app = createApp({
        components: {
            ErrorModal,
            Loader,
        },
        template:
            `
                <div :class="{'is-responsive':isResponsive}">
                    <ErrorModal
                        v-if="config"
                        v-model="showModal"
                        :texts="texts"
                        :config="config"
                        @loadingChanged="loader = $event"
                    />
                    <Loader :visible="loader"/>
                </div>
            `,
        setup() {
            const loader = ref(false);
            const showModal = ref(false);
            const config = ref(null);

            const isResponsive = computed(() => {
                return isTablet();
            });

            const {t} = i18n.global;

            const texts = {
                title: t('error-modal-title'),
                description: t('error-modal-description'),
                button: t('error-modal-button'),
                terms_and_conditions: t('error-modal-terms-and-conditions'),
                email: {
                    label: t('form-email'),
                    placeholder: t('form-email-placeholder'),
                },
                message: {
                    placeholder: t('error-modal-message-placeholder'),
                    default_value: t('error-modal-message-default-value'),
                },
                success: {
                    title: t('error-modal-success-title'),
                    url_text: t('error-modal-success-text'),
                },
            };

            document.addEventListener('openErrorModal', (e) => {
                const emailFromUrl = new URLSearchParams(window.location.search).get('email');
                config.value = {
                    url: `/${window.locale}/error-form`,
                    ...(emailFromUrl && {email: emailFromUrl}),
                    ...e.detail,
                };
                showModal.value = true;
            });

            return {
                loader,
                showModal,
                texts,
                config,
                isResponsive,
            };
        },
    });

    app.use(i18n);
    app.mount(el);
});
